import UserOrGroupSelector from '../../common/selectors/UsersOrGroupSelectors';
import { useContext, useEffect, useState } from 'react';
import {
  Button,
  Field,
  Input,
  Checkbox,
  Spinner,
  Text,
  InfoLabel,
} from '@fluentui/react-components';
import { OpenFilled } from '@fluentui/react-icons';
import { Link } from '@fluentui/react';
import {
  CommandBar,
  DetailsListLayoutMode,
  FontIcon,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  type IColumn,
} from '@fluentui/react';
import { useAtomValue } from 'jotai';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { profileDataAtom } from '../../atoms/authAtoms';
import { appRoles } from '../../authConfig';
import AppContext from '../AppContext';
import {
  getWorkStatuses,
  updateWorkStatus,
  type IWorkStatus,
} from '../../services/workService';

export default function WorkStatusSettings() {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = type === 'work';
  const editingId = Number(searchParams.get('id'));
  const profileData = useAtomValue(profileDataAtom);
  const canEdit = profileData.roles.includes(appRoles.Admin);
  const [statuses, setStatuses] = useState<IWorkStatus[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [selection] = useState<Selection>(
    () =>
      new Selection({
        getKey: (item, index) =>
          item ? String((item as IWorkStatus).id) : `shimmer-${index}`,
        onSelectionChanged: () => {
          const selected = selection.getSelection()[0] as
            | IWorkStatus
            | undefined;
          setSelectedStatusId(selected?.id ?? null);
        },
      })
  );
  const [permissionsStatusId, setPermissionsStatusId] = useState<number | null>(
    null
  );
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    context.setSelectedTab('Work Statuses');
    const controller = new AbortController();
    getWorkStatuses(controller)
      .then((items) => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(items))
          throw new Error('Unable to load work statuses: invalid response.');
        setStatuses(items);
      })
      .catch((error) => {
        if (!controller.signal.aborted) setMessage(String(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  const change = (id: number, values: Partial<IWorkStatus>) =>
    setStatuses((items) =>
      items.map((item) => (item.id === id ? { ...item, ...values } : item))
    );
  const save = async (status: IWorkStatus) => {
    if (!canEdit || busy !== null) return;
    setBusy(status.id);
    setMessage('');
    try {
      await updateWorkStatus(new AbortController(), status.id, {
        clientLabel: status.clientLabel || null,
        managerLabel: status.managerLabel || null,
        contractorLabel: status.contractorLabel || null,
        color: status.color,
        icon: status.icon,
        deliverReportAutomatically: status.deliverReportAutomatically ?? false,
        permissions: status.permissions,
      });
      navigate('/settings/workStatuses');
    } catch (error) {
      setMessage(String(error));
    } finally {
      setBusy(null);
    }
  };
  const columns: IColumn[] = [
    {
      key: 'id',
      name: 'Id',
      fieldName: 'id',
      minWidth: 25,
      maxWidth: 50,
      isResizable: true,
    },
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 250,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'code',
      name: 'Code',
      fieldName: 'code',
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'icon',
      name: 'Icon',
      fieldName: 'icon',
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'color',
      name: 'Color',
      fieldName: 'color',
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
  ];

  if (!isEditing)
    return (
      <Stack styles={{ root: { height: 450, width: 800, maxWidth: '100%' } }}>
        <CommandBar
          items={[
            {
              key: 'editStatus',
              text: 'Edit',
              iconProps: { iconName: 'Edit' },
              disabled: !canEdit || selectedStatusId === null || loading,
              onClick: () => {
                if (selectedStatusId !== null)
                  navigate(`/editStatuses/work?id=${selectedStatusId}`);
              },
            },
          ]}
          ariaLabel="Items actions"
          primaryGroupAriaLabel="Items actions"
        />
        <div role="status">{message}</div>
        <Stack.Item styles={{ root: { overflow: 'auto' } }}>
          <ShimmeredDetailsList
            items={statuses}
            columns={columns}
            setKey="workStatuses"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.single}
            selectionPreservedOnEmptyClick={true}
            selection={selection}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="select row"
            enableShimmer={loading}
            onRenderItemColumn={(item: IWorkStatus, _index, column) => {
              if (column?.key === 'icon')
                return item.icon ? (
                  <FontIcon
                    iconName={item.icon}
                    style={{ width: 25, height: 25, fontSize: 25 }}
                  />
                ) : null;
              if (column?.key === 'color')
                return item.color ? (
                  <span
                    title={item.color}
                    style={{
                      backgroundColor: item.color,
                      height: 24,
                      display: 'block',
                    }}
                  />
                ) : null;
              return (
                <span>
                  {String(item[column?.fieldName as keyof IWorkStatus] ?? '')}
                </span>
              );
            }}
          />
          {!loading && statuses.length === 0 && !message && (
            <Text>No work statuses found.</Text>
          )}
        </Stack.Item>
      </Stack>
    );

  const status = statuses.find((item) => item.id === editingId);
  return (
    <main style={{ maxWidth: 800 }}>
      <CommandBar
        items={[
          {
            key: 'back',
            text: 'Back',
            iconProps: { iconName: 'Back' },
            disabled: busy !== null,
            onClick: () => {
              void navigate('/settings/workStatuses');
            },
          },
        ]}
      />
      <div role="status">{message}</div>
      {loading && <Spinner />}
      {!loading && !status && !message && <Text>Work status not found.</Text>}
      {status && (
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: 16 } }}>
          <h2>
            <FontIcon
              iconName={status.icon || 'Info'}
              style={{ color: status.color }}
            />{' '}
            {status.name}
          </h2>
          <Text>{status.code}</Text>
          <fieldset
            disabled={!canEdit || busy !== null}
            style={{
              border: 0,
              padding: 0,
              margin: 0,
              minWidth: 0,
              display: 'grid',
              gap: 16,
            }}
          >
            {(['clientLabel', 'managerLabel', 'contractorLabel'] as const).map(
              (field, index) => (
                <Field
                  key={field}
                  label={
                    ['Client label', 'Manager label', 'Contractor label'][index]
                  }
                >
                  <Input
                    maxLength={100}
                    value={status[field] || ''}
                    placeholder={status.name}
                    onChange={(_, data) =>
                      change(status.id, { [field]: data.value })
                    }
                  />
                </Field>
              )
            )}
            <Field label="Colour">
              <Input
                value={status.color || ''}
                onChange={(_, data) => change(status.id, { color: data.value })}
              />
            </Field>
            <Field
              label="Icon"
              hint={
                <div>
                  <InfoLabel>Use an icon name from flicon.io</InfoLabel>
                  <Link href="https://flicon.io" target="_blank">
                    <OpenFilled />
                  </Link>
                </div>
              }
            >
              <Input
                value={status.icon || ''}
                onChange={(_, data) => change(status.id, { icon: data.value })}
              />
            </Field>
            {status.allowReportDelivery && (
              <Checkbox
                label="Deliver report automatically after approval"
                checked={status.deliverReportAutomatically ?? false}
                onChange={(_, data) =>
                  change(status.id, {
                    deliverReportAutomatically: data.checked === true,
                  })
                }
              />
            )}
            <Button onClick={() => setPermissionsStatusId(status.id)}>
              Action permissions for current tenant
            </Button>
            <UserOrGroupSelector
              isOpen={permissionsStatusId === status.id}
              hideUsersAndGroupsSelector={() => setPermissionsStatusId(null)}
              permissions={(status.permissions ?? []).map((p) => ({
                id: p.groupId ?? p.userId ?? 0,
                isGroup: p.groupId != null,
                name: String(p.groupId ?? p.userId),
              }))}
              onUserOrGroupSelected={(id, isGroup) =>
                change(status.id, {
                  permissions: [
                    ...(status.permissions ?? []).filter(
                      (p) => (isGroup ? p.groupId : p.userId) !== id
                    ),
                    {
                      userId: isGroup ? null : id,
                      groupId: isGroup ? id : null,
                      tenantId: null,
                    },
                  ],
                })
              }
              onRemovePermission={(id) =>
                change(status.id, {
                  permissions: (status.permissions ?? []).filter(
                    (p) => (p.groupId ?? p.userId) !== id
                  ),
                })
              }
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                appearance="primary"
                disabled={!canEdit || busy !== null}
                onClick={() => void save(status)}
              >
                Save
              </Button>
              <Button
                disabled={busy !== null}
                onClick={() => navigate('/settings/workStatuses')}
              >
                Cancel
              </Button>
            </div>
          </fieldset>
        </Stack>
      )}
    </main>
  );
}
